import { PasswordStrength, commonPasswords, trigraphs } from 'tai-password-strength'
import { UserDto } from '../dtos/user'

enum PasswordStrengthCode {
    VERY_WEAK = 'VERY_WEAK',
    WEAK = 'WEAK',
    REASONABLE = 'REASONABLE',
    STRONG = 'STRONG',
    VERY_STRONG = 'VERY_STRONG',
}

export class PasswordValidators {
    private user: UserDto
    private pwdStrengthValidator = new PasswordStrength()

    constructor(user: UserDto) {
        this.user = user

        this.pwdStrengthValidator.addCommonPasswords(commonPasswords)
        this.pwdStrengthValidator.addTrigraphMap(trigraphs)
    }

    public tooShort = (password = '') => password.length < 12

    public isMissingRequiredCharacters(password = '') {
        return !valueMeetsCharacterReqs(password)
    }

    public isSameAsUserEmail = (password = ''): boolean => {
        return password.toLowerCase() === this.user?.email?.toLowerCase()
    }

    public isSameAsUserName = (password = ''): boolean => {
        return password.toLowerCase() === this.user?.name?.toLowerCase()
    }

    public isWeak = (password = ''): boolean => {
        const pwdCheck = this.pwdStrengthValidator.check(password)
        const strength = pwdCheck.strengthCode

        return strength === PasswordStrengthCode.VERY_WEAK || strength === PasswordStrengthCode.WEAK
    }

    public score = (password = ''): string => {
        return this.readableScore(this.pwdStrengthValidator.check(password).strengthCode)
    }

    public readableScore = (strength = ''): string => {
        switch (strength) {
            case PasswordStrengthCode.VERY_STRONG:
                return 'very strong'
            case PasswordStrengthCode.STRONG:
                return 'strong'
            case PasswordStrengthCode.REASONABLE:
                return 'medium'
            case PasswordStrengthCode.WEAK:
                return 'weak'
            case PasswordStrengthCode.VERY_WEAK:
            default:
                return 'very weak'
        }
    }

    public validate = (_rule: any, value: any): Promise<void> => {
        const password = value || ''

        if (!password) {
            return Promise.reject(new Error('Password may not be left empty.'))
        }

        // The popover anchored to the field lists each requirement and its pass/fail
        // state, so one generic message is enough to block submission here.
        const meetsRequirements =
            !this.tooShort(password) &&
            !this.isMissingRequiredCharacters(password) &&
            !this.isSameAsUserEmail(password) &&
            !this.isSameAsUserName(password) &&
            !this.isWeak(password)

        return meetsRequirements ? Promise.resolve() : Promise.reject(new Error('Password does not meet requirements.'))
    }
}

export type CharacterRequirements = {
    lowercase: boolean
    uppercase: boolean
    number: boolean
    special: boolean
}

/**
 * How many of the four character classes a password must use. Keep every requirement
 * check and every requirement display derived from this one value — a threshold that
 * disagrees with the copy shown to the user rejects passwords the UI called valid.
 */
export const REQUIRED_CHARACTER_CLASSES = 3

export function valueMeetsCharacterReqs(value: string, getRaw: true): CharacterRequirements
export function valueMeetsCharacterReqs(value: string, getRaw?: false): boolean
export function valueMeetsCharacterReqs(value = '', getRaw?: boolean): CharacterRequirements | boolean {
    const requirements: CharacterRequirements = {
        lowercase: /[a-z]/.test(value),
        uppercase: /[A-Z]/.test(value),
        number: /[0-9]/.test(value),
        special: /['!@#$%^&*']/.test(value),
    }

    if (getRaw) {
        return requirements
    }

    return Object.values(requirements).filter(Boolean).length >= REQUIRED_CHARACTER_CLASSES
}
